import type { MediaItem } from "../../sources/types";

/**
 * The one place item facts become display strings. Every surface — cards,
 * heroes, episode rows — pulls from here so movies, shows, seasons and
 * episodes read consistently everywhere.
 */

export function formatRuntime(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

/** "3:41" resume offset. */
export function formatOffset(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function seasonsLabel(n: number | null | undefined): string | null {
  if (n == null || n <= 0) return null;
  return `${n} season${n === 1 ? "" : "s"}`;
}

export function episodesLabel(n: number | null | undefined): string | null {
  if (n == null || n <= 0) return null;
  return `${n} episode${n === 1 ? "" : "s"}`;
}

/** "S1 · E3" from whatever hierarchy numbers an episode has. */
/** "S1 · E3" from raw hierarchy numbers (dashboard sessions, episode rows). */
export function seasonEpisodePair(
  parentIndex: number | null | undefined,
  index: number | null | undefined,
): string {
  return [parentIndex != null ? `S${parentIndex}` : null, index != null ? `E${index}` : null]
    .filter(Boolean)
    .join(" · ");
}

export function seasonEpisodeTag(item: MediaItem): string {
  return seasonEpisodePair(item.episode?.parentIndex, item.episode?.index);
}

/** Poster caption, branching on the item's type — shows "N seasons",
 * episodes "Show · S1 · E3", movies the year. */
export function captionFor(item: MediaItem): string {
  if (item.type === "show") {
    const label = seasonsLabel(item.seasonCount ?? item.childCount);
    if (label) return label;
    return item.year ? String(item.year) : "Series";
  }
  if (item.type === "episode") {
    const caption = [item.episode?.grandparentTitle, seasonEpisodeTag(item)]
      .filter(Boolean)
      .join(" · ");
    return caption || (item.year ? String(item.year) : "");
  }
  if (item.type === "season") return episodesLabel(item.childCount) ?? "";
  return item.year ? String(item.year) : "";
}

/** A card's caption lines; secondary/tertiary null when there is nothing to say. */
export interface CardLines {
  primary: string;
  secondary: string | null;
  tertiary: string | null;
}

/** The show-art episode card's three lines — show / episode title /
 * "S1 · E5" (Plex's Continue Watching card; historyCaption's shape). With
 * no show title the episode title leads and the pair drops to line two. */
export function episodeCardLines(item: MediaItem): CardLines {
  const pair = seasonEpisodeTag(item) || null;
  const show = item.episode?.grandparentTitle;
  if (show) return { primary: show, secondary: item.title, tertiary: pair };
  return { primary: item.title, secondary: pair, tertiary: null };
}

/** The hero's facts, split so the hero can render the rating as a chip and
 * the genres muted instead of one flat dot-joined string. */
export function metaFacts(item: MediaItem): {
  parts: string[];
  rating: string | null;
  genres: string[];
} {
  if (item.type === "episode") {
    const ep = item.episode;
    return {
      parts: [
        ep?.parentIndex != null ? `Season ${ep.parentIndex}` : null,
        ep?.index != null ? `Episode ${ep.index}` : null,
        item.airDate || null,
        item.runtimeMs >= 60_000 ? formatRuntime(item.runtimeMs) : null,
      ].filter((p): p is string => Boolean(p)),
      rating: item.contentRating || null,
      genres: [],
    };
  }
  if (item.type === "season") {
    return {
      parts: [item.year ? String(item.year) : null, episodesLabel(item.childCount)].filter(
        (p): p is string => Boolean(p),
      ),
      rating: null,
      genres: [],
    };
  }
  return {
    parts: [
      item.year ? String(item.year) : null,
      item.type === "show"
        ? seasonsLabel(item.seasonCount ?? item.childCount)
        : item.runtimeMs >= 60_000
          ? formatRuntime(item.runtimeMs)
          : null,
    ].filter((p): p is string => Boolean(p)),
    rating: item.contentRating || null,
    genres: item.genres,
  };
}
